/**
 * A Pages container's page edits: add, delete, duplicate, reorder and rename.
 * `pages` is positional, so each edit keeps it in step with the page order,
 * and its command takes the whole edit back in one undo, landing on the page
 * that showed before it; redo lands where the edit did.
 *
 * Each operation applies its change, announces it on the container, and
 * returns the command without pushing it. Null means nothing changed: no
 * such page, a container without Pages, or a scene that can't be edited now
 * (play, an undo running). Delete and duplicate go through the authoring
 * `deleteObjects` and `duplicateObjects`, and return their refusals. A host
 * leaves play first, and settles any animation the navigation interrupted.
 */

import type { SceneObject } from '../../core/SceneObject';
import { Pages } from '../../behaviors/pages';
import type { UndoCommand } from '../undo/UndoCommand';
import { ActionCommand } from '../undo/ActionCommand';
import { CreateObjectCommand } from '../undo/CreateObjectCommand';
import { RenameCommand } from '../undo/RenameCommand';
import { editRefusal } from '../authoring/checks';
import { deleteObjects, duplicateObjects } from '../authoring/objectOps';
import { bundle, capture, descendantEntries } from '../authoring/steps';
import type { AuthoringPorts, OpResult } from '../authoring/types';
import { commitPages, findSharedGroup, notifyPagesChanged, pagingOf, takeBack } from './sharedChildren';
import { pageChildrenOf, setSharedIntent } from './pageContext';
import { inheritDeckPreset } from './pagePresets';
import { cloneEntryForDuplicate, moveItem, remapPagesForReorder, retargetDataForMove, spliceForDelete } from './pagesModel';

/**
 * Add a page after the last one, show it and select it, so what the user
 * makes next lands on it. When the deck carries a transition preset, the new
 * page takes it.
 */
export async function addPage(container: SceneObject): Promise<{ page: SceneObject; command: UndoCommand } | null> {
    const pages = editablePages(container);
    if (!pages) return null;
    const scene = container.traceScene;
    const shownBefore = pages.getActivePageIndex();
    const count = pageChildrenOf(container).length;
    const page = await scene.createSceneObject(`Page ${count + 1}`, container);
    // The container may have gone while the page was made.
    const discard = (): null => {
        if (!page.transform.isDisposed()) scene.deleteSceneObject(page);
        return null;
    };
    if (container.transform.isDisposed()) return discard();
    page.setActive(count === 0);
    let inherited: UndoCommand | null;
    try {
        inherited = await inheritDeckPreset(container, pageChildrenOf(container).indexOf(page));
    } catch (error) {
        discard();
        throw error;
    }
    if (container.transform.isDisposed()) return discard();
    // Captured after the preset, so redo makes the page with its states.
    const created = new CreateObjectCommand(page, container, capture(page), descendantEntries(page));
    const index = pageChildrenOf(container).indexOf(page);
    show(container, pages, index);
    setSharedIntent(container, false);
    scene.select(page);
    const steps = [navigateOnUndo(container, pages, shownBefore), created];
    if (inherited) steps.push(inherited);
    steps.push(navigateOnRedo(container, pages, index));
    return { page, command: bundle(`Add ${page.name}`, steps) };
}

/**
 * Delete page `index`. Deleting the page showing shows its neighbour. When
 * the selection went with the page, the page showing is selected, or, when
 * none is left, the Shared context is entered. `ports` is passed to
 * `deleteObjects`.
 */
export async function deletePage(container: SceneObject, index: number, ports: AuthoringPorts = {}): Promise<OpResult<{ deleted: SceneObject }> | null> {
    const pages = editablePages(container);
    const pageChildren = pageChildrenOf(container);
    const page = pageChildren[index];
    if (!pages || !page) return null;
    const scene = container.traceScene;
    const shownBefore = pages.getActivePageIndex();
    if (index === shownBefore && pageChildren.length > 1) pages.showPage(index > 0 ? index - 1 : 1, false);

    // Undo makes the page again as the last child; pages are positional, so
    // the order before the delete comes back with it.
    const registry = scene.objectIdentityRegistry;
    const order = container.getChildren().map(child => registry.refFor(child));
    const restoreOrder = new ActionCommand('Restore page order', () => {
        reparentInOrder(container, order.map(ref => ref.get()));
    }, () => {});
    const before = pages.getData();
    const after = { ...before, pages: spliceForDelete(before.pages, index, pageChildren.length) };

    const showBefore = (): void => {
        if (shownBefore >= 0 && shownBefore < pageChildren.length) pages.showPage(shownBefore, false);
    };
    let result: Awaited<ReturnType<typeof deleteObjects>>;
    try {
        result = await deleteObjects(page, ports);
    } catch (error) {
        showBefore();
        throw error;
    }
    if ('refused' in result) {
        showBefore();
        return result;
    }
    const steps = [navigateOnUndo(container, pages, shownBefore), restoreOrder, result.command];
    try {
        const committed = await commitPages(container, pages, after);
        if (committed) steps.push(committed);
    } catch (error) {
        await takeBack(container, steps, error);
        throw error;
    }
    // The page showing may have moved down one, or been the one deleted.
    syncActivePageToAuthored(container);
    const shownAfter = pageChildrenOf(container).length > 0 ? pages.getActivePageIndex() : -1;
    steps.push(new ActionCommand('Select pages context', () => selectPagesContext(container, pages), () => {
        if (shownAfter >= 0) pages.showPage(shownAfter, false);
        selectPagesContext(container, pages);
    }));
    selectPagesContext(container, pages);
    notifyPagesChanged(container);
    return { deleted: page, command: bundle(`Delete page ${page.name}`, steps) };
}

/**
 * Copy page `index` right after it and show the copy. Its `pages` entry is
 * the source's, retargeted into the copy.
 */
export async function duplicatePage(container: SceneObject, index: number): Promise<OpResult<{ copy: SceneObject }> | null> {
    const pages = editablePages(container);
    const pageChildren = pageChildrenOf(container);
    const source = pageChildren[index];
    if (!pages || !source) return null;
    const shownBefore = pages.getActivePageIndex();
    const result = await duplicateObjects(source);
    if ('refused' in result) return result;
    const copy = result.copies.get(source)!;
    copy.setActive(false);
    // Recorded after hiding it, so redo makes the copy hidden too.
    const steps: UndoCommand[] = [
        navigateOnUndo(container, pages, shownBefore),
        new CreateObjectCommand(copy, container, capture(copy), descendantEntries(copy)),
    ];
    const slot = index + 1;
    try {
        const withCopy = [...pageChildren, copy];
        steps.push(reorder(container, pages, withCopy, moveItem(withCopy, withCopy.length - 1, slot)));
        const before = pages.getData();
        const appended = cloneEntryForDuplicate(before.pages, index, source.name, copy.name, pageChildren.length);
        const indexMap = moveItem(withCopy.map((_, i) => i), withCopy.length - 1, slot);
        const committed = await commitPages(container, pages, { ...before, pages: remapPagesForReorder(appended, indexMap, withCopy.length) });
        if (committed) steps.push(committed);
    } catch (error) {
        await takeBack(container, steps.slice(1), error);
        throw error;
    }
    show(container, pages, slot);
    steps.push(navigateOnRedo(container, pages, slot));
    return { copy, command: bundle(`Duplicate page ${source.name}`, steps) };
}

/** Move page `from` into the gap `slot` (0 to the page count), keeping the page showing on screen. */
export async function reorderPages(container: SceneObject, from: number, slot: number): Promise<UndoCommand | null> {
    const pages = editablePages(container);
    const pageChildren = pageChildrenOf(container);
    if (!pages || !Number.isInteger(from) || !Number.isInteger(slot) || from < 0 || from >= pageChildren.length) return null;
    const gap = Math.max(0, Math.min(slot, pageChildren.length));
    const next = moveItem(pageChildren, from, gap);
    if (next.every((page, i) => page === pageChildren[i])) return null;
    const before = pages.getData();
    const indexMap = moveItem(pageChildren.map((_, i) => i), from, gap);
    const steps = [reorder(container, pages, pageChildren, next)];
    try {
        const committed = await commitPages(container, pages, { ...before, pages: remapPagesForReorder(before.pages, indexMap, pageChildren.length) });
        if (committed) steps.push(committed);
    } catch (error) {
        await takeBack(container, steps, error);
        throw error;
    }
    notifyPagesChanged(container);
    return bundle('Reorder pages', steps);
}

/**
 * Rename page `index` to `name`, or the name the scene gives it when a
 * sibling has it, and rewrite the container's stored paths in the same step.
 */
export async function renamePage(container: SceneObject, index: number, name: string): Promise<UndoCommand | null> {
    const pages = editablePages(container);
    const page = pageChildrenOf(container)[index];
    if (!pages || !page || page.name === name) return null;
    const command = await renameObject(page, name);
    if (command) notifyPagesChanged(container);
    return command;
}

/**
 * Rename `object` to `name`, and rewrite the paths every Pages container
 * above it stores (`shared` and the page assignments) in the same step, so
 * they still name it. Null when the object already has the name, or the
 * scene refuses it: empty, with a `/`, or a sibling's. The caller asks whether
 * the scene can be edited now.
 */
export async function renameObject(object: SceneObject, name: string): Promise<UndoCommand | null> {
    const scene = object.traceScene;
    const decks: { container: SceneObject; from: string }[] = [];
    for (let node = object.getParent(); node; node = node.getParent()) {
        const from = pagingOf(node) ? scene.getRelativePath(object, node) : null;
        if (from) decks.push({ container: node, from });
    }
    const oldName = object.name;
    if (!scene.renameSceneObject(object, name) || object.name === oldName) return null;
    const steps: UndoCommand[] = [new RenameCommand(object, oldName, object.name)];
    for (const { container, from } of decks) {
        const pages = pagingOf(container)!;
        const to = scene.getRelativePath(object, container);
        try {
            const committed = to ? await commitPages(container, pages, retargetDataForMove(pages.getData(), from, to)) : null;
            if (committed) steps.push(committed);
        } catch (error) {
            await takeBack(container, steps, error);
            throw error;
        }
    }
    return bundle(`Rename ${object.name}`, steps);
}

/**
 * Show the page whose authored `active` flag is set, when the container's
 * active index names another: they part after a load, a play session or a
 * delete. Announces a change.
 */
export function syncActivePageToAuthored(container: SceneObject): void {
    const pages = container.getBehavior(Pages);
    if (!pages) return;
    const authored = pageChildrenOf(container).findIndex(page => page.getSceneObjectData().active === true);
    if (authored >= 0 && authored !== pages.getActivePageIndex()) {
        pages.showPage(authored, false);
        notifyPagesChanged(container);
    }
}

// ── Steps ────────────────────────────────────────────────────────────────────

/** The container's Pages when it pages its own children and the scene can be edited now. */
function editablePages(container: SceneObject): Pages | null {
    const pages = pagingOf(container);
    return pages && !editRefusal(container.traceScene, [container]) ? pages : null;
}

function show(container: SceneObject, pages: Pages, index: number): void {
    if (index < 0) return;
    pages.showPage(index, false);
    notifyPagesChanged(container);
}

/** First in a bundle, so its undo runs last: show the page that showed before the edit. */
function navigateOnUndo(container: SceneObject, pages: Pages, index: number): UndoCommand {
    return new ActionCommand('Restore active page', () => show(container, pages, index), () => {});
}

/** Last in a bundle: redo shows the page the edit showed. By index, since redo makes new objects. */
function navigateOnRedo(container: SceneObject, pages: Pages, index: number): UndoCommand {
    return new ActionCommand('Show page', () => {}, () => show(container, pages, index));
}

/** Put `next` in the page order now, returning the command that switches between it and `before`. The page showing stays on screen. */
function reorder(container: SceneObject, pages: Pages, before: readonly SceneObject[], next: readonly SceneObject[]): UndoCommand {
    const registry = container.traceScene.objectIdentityRegistry;
    const shown = pageChildrenOf(container)[pages.getActivePageIndex()];
    const shownRef = shown ? registry.refFor(shown) : null;
    const beforeRefs = before.map(page => registry.refFor(page));
    const nextRefs = next.map(page => registry.refFor(page));
    const apply = (refs: typeof beforeRefs): void => {
        reparentInOrder(container, refs.map(ref => ref.get()));
        const index = shownRef ? pageChildrenOf(container).indexOf(shownRef.get()!) : -1;
        if (index >= 0) pages.showPage(index, false);
        notifyPagesChanged(container);
    };
    apply(nextRefs);
    return new ActionCommand('Reorder pages', () => apply(beforeRefs), () => apply(nextRefs));
}

/** Make the live ones of `children` the container's last children, in this order. */
function reparentInOrder(container: SceneObject, children: readonly (SceneObject | null | undefined)[]): void {
    const live = children.filter((child): child is SceneObject => !!child && !child.transform.isDisposed());
    for (const child of live) child.transform.parent = null;
    for (const child of live) child.setParent(container);
}

/**
 * When the selection is gone, select the page showing, or with no pages
 * left, enter the Shared context: select the Shared group or the container.
 */
function selectPagesContext(container: SceneObject, pages: Pages): void {
    if (container.transform.isDisposed()) return;
    const scene = container.traceScene;
    const selected = scene.getSelectedObject();
    if (selected && !selected.transform.isDisposed()) return;
    const remaining = pageChildrenOf(container);
    if (remaining.length > 0) {
        scene.select(remaining[Math.min(Math.max(pages.getActivePageIndex(), 0), remaining.length - 1)]);
        return;
    }
    setSharedIntent(container, true);
    scene.select(findSharedGroup(container) ?? container);
}
